/**
 * Analytics Redux Middleware
 *
 * Intercepts every dispatched Redux action and checks the `reduxActionEvents`
 * config for a matching event mapping. If found (and optional condition passes),
 * the event is tracked automatically.
 *
 * SAFETY: `next(action)` is ALWAYS called first. Analytics logic is wrapped
 * in try/catch so it can never break the application.
 *
 * @module @ideoz/core/analytics/middleware
 */

import type { Middleware } from '@reduxjs/toolkit';
import { analyticsConfig } from './analytics.config';
import { trackEvent } from './analytics.service';
import type { ReduxAction, ReduxState } from './analytics.types';

/**
 * Redux middleware that auto-tracks events from config.
 */
export const analyticsMiddleware: Middleware = (store) => (next) => (action) => {
  // Capture state BEFORE the action is processed — needed for conditions that
  // depend on pre-action state (e.g. "was a prototype being created?")
  const prevState = store.getState() as ReduxState;

  // ALWAYS pass the action through first — app works even if analytics is broken
  const result = next(action);

  // Then try to track — wrapped in try/catch
  try {
    const nextState = store.getState() as ReduxState;
    trackFromConfig(action as ReduxAction, nextState, prevState);
  } catch (error) {
    console.warn('Analytics middleware error (non-blocking):', error);
  }

  return result;
};

/**
 * Look up the action type in the config and fire the matching event.
 *
 * @param action  - The dispatched Redux action
 * @param state   - State AFTER the action was reduced
 * @param prevState - State BEFORE the action was reduced (for pre-condition checks)
 */
function trackFromConfig(action: ReduxAction, state: ReduxState, prevState: ReduxState): void {
  if (!action || !action.type) return;

  const mapping = analyticsConfig.reduxActionEvents[action.type];
  if (!mapping) return;

  // Check optional condition (receives action, current state, AND previous state)
  if (mapping.condition && !mapping.condition(action, prevState)) return;

  // Extract properties
  const extracted = mapping.extractProperties
    ? mapping.extractProperties(action, state)
    : {};

  trackEvent(mapping.event_name, extracted);
}
